import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Edit2, AlertTriangle, CheckCircle2, Clock, Wrench, Calendar, Settings } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Progress } from '../components/ui/Progress';
import { mockVehicles, mockClients, mockServiceOrders, mockMechanics } from '../data/mockData';
import { formatPlate, formatDate, formatCurrency } from '../lib/utils';
export function VehicleDetail() {
  const {
    id
  } = useParams();
  const vehicle = mockVehicles.find(v => v.id === id);
  if (!vehicle) {
    return <div className="flex h-full items-center justify-center flex-col gap-4" data-id="element-1035">
        <h2 className="text-2xl font-bold" data-id="element-1036">Veículo não encontrado</h2>
        <Button asChild data-id="element-1037">
          <Link to="/app/veiculos" data-id="element-1038">Voltar para Veículos</Link>
        </Button>
      </div>;
  }
  const client = mockClients.find(c => c.id === vehicle.clientId);
  const vehicleOrders = mockServiceOrders.filter(os => os.vehicleId === vehicle.id).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  // Circular Progress SVG calculation
  const healthPercentage = 78;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - healthPercentage / 100 * circumference;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="h-full flex flex-col space-y-6 pb-8" data-id="element-1039">
      {/* Top Bar */}
      <div className="flex items-center justify-between" data-id="element-1040">
        <Button variant="ghost" size="icon" asChild className="rounded-full" data-id="element-1041">
          <Link to="/app/veiculos" data-id="element-1042">
            <ArrowLeft className="h-5 w-5" data-id="element-1043" />
          </Link>
        </Button>
        <div className="flex items-center gap-2" data-id="element-1044">
          <Button variant="outline" size="sm" className="gap-2" data-id="element-1045">
            <Edit2 className="h-4 w-4" data-id="element-1046" /> Editar
          </Button>
          <Button size="sm" className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-id="element-1047">
            <Plus className="h-4 w-4" data-id="element-1048" /> Nova OS
          </Button>
        </div>
      </div>

      {/* Hero Strip */}
      <Card className="overflow-hidden border-none shadow-lg bg-card/50 backdrop-blur-sm" data-id="element-1049">
        <div className="flex flex-col md:flex-row" data-id="element-1050">
          <div className="w-full md:w-1/3 lg:w-1/4 h-48 md:h-auto relative bg-muted" data-id="element-1051">
            <img src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80" alt="Vehicle" className="absolute inset-0 w-full h-full object-cover" data-id="element-1052" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent md:hidden" data-id="element-1053" />
          </div>
          <div className="flex-1 p-6 md:p-8 flex flex-col justify-center" data-id="element-1054">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1055">
              <div data-id="element-1056">
                <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-2" data-id="element-1057">
                  {vehicle.brand} {vehicle.model}
                </h1>
                <div className="flex flex-wrap items-center gap-3" data-id="element-1058">
                  <Badge variant="secondary" className="font-mono text-lg px-3 py-1 bg-background/50 backdrop-blur-md border-border/50" data-id="element-1059">
                    {formatPlate(vehicle.plate)}
                  </Badge>
                  <span className="text-muted-foreground font-medium" data-id="element-1060">
                    {vehicle.year}
                  </span>
                  <span className="text-muted-foreground font-medium" data-id="element-1061">•</span>
                  <span className="text-muted-foreground font-medium" data-id="element-1062">
                    {vehicle.engine}
                  </span>
                  <span className="text-muted-foreground font-medium" data-id="element-1063">•</span>
                  <span className="text-muted-foreground font-medium" data-id="element-1064">
                    {vehicle.mileage.toLocaleString('pt-BR')} km
                  </span>
                </div>
              </div>
              <div className="text-left md:text-right" data-id="element-1065">
                <p className="text-sm text-muted-foreground mb-1" data-id="element-1066">
                  Proprietário
                </p>
                <Link to={`/app/clientes/${client?.id}`} className="font-medium text-primary hover:underline" data-id="element-1067">
                  {client?.name}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1068">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1069">
          {/* Saúde do Veículo */}
          <Card data-id="element-1070">
            <CardHeader data-id="element-1071">
              <CardTitle className="flex items-center gap-2" data-id="element-1072">
                <Settings className="h-5 w-5 text-primary" data-id="element-1073" /> Saúde do Veículo
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-1074">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-1075">
                {/* Circular Progress */}
                <div className="relative flex items-center justify-center shrink-0" data-id="element-1076">
                  <svg className="w-32 h-32 transform -rotate-90" data-id="element-1077">
                    <circle cx="64" cy="64" r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" className="text-muted" data-id="element-1078" />
                    <circle cx="64" cy="64" r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="text-primary transition-all duration-1000 ease-out" strokeLinecap="round" data-id="element-1079" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1080">
                    <span className="text-3xl font-bold" data-id="element-1081">
                      {healthPercentage}%
                    </span>
                    <span className="text-xs text-muted-foreground" data-id="element-1082">Geral</span>
                  </div>
                </div>

                {/* Subsystems */}
                <div className="flex-1 w-full space-y-4" data-id="element-1083">
                  {[{
                  name: 'Motor',
                  val: 92,
                  status: 'ok'
                }, {
                  name: 'Freios',
                  val: 65,
                  status: 'warn'
                }, {
                  name: 'Suspensão',
                  val: 80,
                  status: 'ok'
                }, {
                  name: 'Pneus',
                  val: 45,
                  status: 'alert'
                }, {
                  name: 'Bateria',
                  val: 88,
                  status: 'ok'
                }, {
                  name: 'Óleo',
                  val: 30,
                  status: 'alert'
                }].map((sys, i) => <div key={i} className="space-y-1.5" data-id="element-1084">
                      <div className="flex justify-between text-sm" data-id="element-1085">
                        <span className="font-medium" data-id="element-1086">{sys.name}</span>
                        <span className="text-muted-foreground" data-id="element-1087">
                          {sys.val}%
                        </span>
                      </div>
                      <Progress value={sys.val} className="h-2" indicatorClassName={sys.status === 'ok' ? 'bg-emerald-500' : sys.status === 'warn' ? 'bg-automotive-orange' : 'bg-destructive'} data-id="element-1088" />
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Histórico de Manutenção */}
          <Card data-id="element-1089">
            <CardHeader data-id="element-1090">
              <CardTitle className="flex items-center gap-2" data-id="element-1091">
                <Clock className="h-5 w-5 text-primary" data-id="element-1092" /> Histórico de
                Manutenção
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-1093">
              {vehicleOrders.length > 0 ? <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent" data-id="element-1094">
                  {vehicleOrders.map((os, i) => {
                const mech = mockMechanics.find(m => m.id === os.mechanicId);
                return <div key={os.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1095">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-background bg-muted text-muted-foreground shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow z-10" data-id="element-1096">
                          <Wrench className="h-4 w-4" data-id="element-1097" />
                        </div>
                        <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)]" data-id="element-1098">
                          <Link to={`/app/ordens/${os.id}`} data-id="element-1099">
                            <Card className="hover:border-primary/50 transition-colors cursor-pointer" data-id="element-1100">
                              <CardContent className="p-4" data-id="element-1101">
                                <div className="flex justify-between items-start mb-2" data-id="element-1102">
                                  <Badge variant="secondary" className="font-mono" data-id="element-1103">
                                    {os.number}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground flex items-center gap-1" data-id="element-1104">
                                    <Calendar className="h-3 w-3" data-id="element-1105" />{' '}
                                    {formatDate(os.createdAt)}
                                  </span>
                                </div>
                                <p className="text-sm font-medium mb-3 line-clamp-2" data-id="element-1106">
                                  {os.notes || 'Revisão geral'}
                                </p>
                                <div className="flex justify-between items-center text-sm border-t border-border pt-3" data-id="element-1107">
                                  <span className="text-muted-foreground" data-id="element-1108">
                                    {mech?.name || 'Oficina'}
                                  </span>
                                  <span className="font-bold text-foreground" data-id="element-1109">
                                    {formatCurrency(os.total)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        </div>
                      </div>;
              })}
                </div> : <div className="text-center py-8 text-muted-foreground" data-id="element-1110">
                  Nenhum histórico de manutenção encontrado para este veículo.
                </div>}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6" data-id="element-1111">
          {/* Alertas Inteligentes */}
          <Card className="border-automotive-orange/20 bg-automotive-orange/5" data-id="element-1112">
            <CardHeader data-id="element-1113">
              <CardTitle className="flex items-center gap-2 text-automotive-orange" data-id="element-1114">
                <AlertTriangle className="h-5 w-5" data-id="element-1115" /> Alertas Inteligentes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-id="element-1116">
              <div className="flex gap-3 p-3 rounded-lg bg-background border border-border" data-id="element-1117">
                <AlertTriangle className="h-5 w-5 text-automotive-orange shrink-0 mt-0.5" data-id="element-1118" />
                <div data-id="element-1119">
                  <p className="text-sm font-medium" data-id="element-1120">
                    Troca de óleo recomendada
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-id="element-1121">
                    Nível crítico (30%). Última troca há 11 meses.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-3 rounded-lg bg-background border border-border" data-id="element-1122">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" data-id="element-1123" />
                <div data-id="element-1124">
                  <p className="text-sm font-medium" data-id="element-1125">
                    Pneus precisam de rodízio
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-id="element-1126">
                    Desgaste irregular detectado na última OS.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-3 rounded-lg bg-background border border-border" data-id="element-1127">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-1128" />
                <div data-id="element-1129">
                  <p className="text-sm font-medium" data-id="element-1130">
                    Revisão de 50.000 km próxima
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-id="element-1131">
                    Faltam 5.000 km. Agende com antecedência.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Especificações Técnicas */}
          <Card data-id="element-1132">
            <CardHeader data-id="element-1133">
              <CardTitle data-id="element-1134">Especificações Técnicas</CardTitle>
            </CardHeader>
            <CardContent data-id="element-1135">
              <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm" data-id="element-1136">
                <div data-id="element-1137">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1138">
                    Marca
                  </span>
                  <span className="font-medium" data-id="element-1139">{vehicle.brand}</span>
                </div>
                <div data-id="element-1140">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1141">
                    Modelo
                  </span>
                  <span className="font-medium" data-id="element-1142">{vehicle.model}</span>
                </div>
                <div data-id="element-1143">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1144">
                    Ano
                  </span>
                  <span className="font-medium" data-id="element-1145">{vehicle.year}</span>
                </div>
                <div data-id="element-1146">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1147">
                    Motor
                  </span>
                  <span className="font-medium" data-id="element-1148">{vehicle.engine}</span>
                </div>
                <div data-id="element-1149">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1150">
                    Combustível
                  </span>
                  <span className="font-medium" data-id="element-1151">{vehicle.fuel}</span>
                </div>
                <div data-id="element-1152">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1153">
                    Cor
                  </span>
                  <span className="font-medium" data-id="element-1154">Prata</span>
                </div>
                <div className="col-span-2" data-id="element-1155">
                  <span className="text-muted-foreground block text-xs uppercase tracking-wider mb-1" data-id="element-1156">
                    VIN / Chassi
                  </span>
                  <span className="font-mono font-medium" data-id="element-1157">{vehicle.vin}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>;
}